import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Calculator } from 'lucide-react';
import ROICalculator from './components/ROICalculator'; // Import the ROI Calculator component

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState('yearly'); // 'monthly' or 'yearly'
  const [showCalculator, setShowCalculator] = useState(false); // To toggle ROI calculator visibility

  // Calculate yearly price with exactly 20% off
  const calculateYearlyPrice = (monthlyPrice) => {
    return Math.round(monthlyPrice * 12 * 0.8);
  };

  // Pricing data with corrected yearly prices
  const plans = [
    {
      name: 'Starter',
      description: 'Perfect for freelancers and small businesses',
      monthlyPrice: 29,
      yearlyPrice: calculateYearlyPrice(29), // Now correctly 278
      features: [
        'Single client portal',
        'Unlimited status lookups',
        'Universal spreadsheet integration',
        'Basic customization',
        'Email support',
      ],
      notIncluded: [
        'Multiple portals',
        'Custom domain',
        'Advanced analytics',
        'White-label option',
        'Priority support',
      ],
      cta: 'Start Free Trial',
      highlight: false,
    },
    {
      name: 'Professional',
      description: 'Best for growing businesses with multiple clients',
      monthlyPrice: 79,
      yearlyPrice: calculateYearlyPrice(79), // Now correctly 758
      features: [
        'Up to 5 client portals',
        'Unlimited status lookups',
        'Universal spreadsheet integration',
        'Full branding customization',
        'Basic analytics',
        'Email & chat support',
        'Custom status workflows',
      ],
      notIncluded: ['Custom domain', 'White-label option', 'Priority support'],
      cta: 'Start Free Trial',
      highlight: true,
    },
    {
      name: 'Business',
      description: 'For established businesses with complex needs',
      monthlyPrice: 199,
      yearlyPrice: calculateYearlyPrice(199), // Now correctly 1910
      features: [
        'Unlimited client portals',
        'Unlimited status lookups',
        'Universal spreadsheet integration',
        'Full branding customization',
        'Advanced analytics & reporting',
        'Priority support',
        'Custom domain support',
        'White-label option',
        'API access',
      ],
      notIncluded: [],
      cta: 'Start Free Trial',
      highlight: false,
    },
  ];

  const enterpriseOptions = [
    {
      name: 'Enterprise SaaS',
      description:
        'Dedicated cloud deployment with premium features and support',
      pricing: 'Contact Sales',
      subtitle: 'Custom pricing based on your organization size and needs',
      badge: 'Most Scalable',
      features: [
        'Private cloud deployment on dedicated infrastructure',
        'Unlimited client portals and users',
        'Volume discounts for large organizations',
        'Custom feature development and integrations',
        'Dedicated customer success manager',
        'Priority support with 99.9% uptime SLA',
        'Advanced security and compliance features',
        'Custom domain and complete white-labeling',
        'API access and webhook integrations',
      ],
    },
    {
      name: 'Enterprise License',
      description:
        'Perpetual usage rights for unlimited deployment flexibility',
      pricing: 'Contact Sales',
      subtitle: 'One-time licensing fee based on deployment scope',
      badge: 'Best Value',
      features: [
        'Perpetual usage rights with no monthly fees',
        'Deploy on your own servers and infrastructure',
        'Complete white-label customization',
        'Unlimited client portals and status lookups',
        'Full platform functionality included',
        'Self-hosted with your own API keys',
        'Optional ongoing support contracts available',
        'Custom development services available',
        'Enterprise training and implementation support',
      ],
    },
  ];

  // Calculate annual savings for each plan
  const calculateAnnualSavings = (monthlyPrice) => {
    const annualCost = monthlyPrice * 12;
    const discountedAnnualCost = calculateYearlyPrice(monthlyPrice);
    return annualCost - discountedAnnualCost;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      {/* Header */}
      <header className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight">
            Simple, Transparent Pricing
          </h1>
          <p className="mt-5 max-w-xl mx-auto text-xl text-gray-500">
            Choose the plan that's right for your business. All plans include a
            14-day free trial.
          </p>

          {/* Billing toggle */}
          <div className="mt-12 flex justify-center">
            <div className="relative bg-gray-100 p-1 rounded-lg inline-flex">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  billingCycle === 'monthly'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  billingCycle === 'yearly'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Yearly{' '}
                <span className="text-green-600 font-semibold">(Save 20%)</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Pricing Cards */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-lg shadow-lg overflow-hidden ${
                  plan.highlight ? 'ring-2 ring-blue-600' : ''
                }`}
              >
                {plan.highlight && (
                  <div className="absolute top-0 inset-x-0 px-4 py-1 bg-blue-600 text-white text-center text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <div className={`px-6 py-8 ${plan.highlight ? 'pt-12' : ''}`}>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {plan.name}
                  </h3>
                  <p className="mt-2 text-gray-500">{plan.description}</p>
                  <p className="mt-6">
                    <span className="text-5xl font-extrabold text-gray-900">
                      $
                      {billingCycle === 'monthly'
                        ? plan.monthlyPrice
                        : plan.yearlyPrice}
                    </span>
                    <span className="text-base text-gray-500">
                      {billingCycle === 'monthly' ? '/mo' : '/year'}
                    </span>
                  </p>
                  {billingCycle === 'yearly' && (
                    <p className="mt-2 text-sm text-green-600 font-medium">
                      Save ${calculateAnnualSavings(plan.monthlyPrice)} per year
                    </p>
                  )}
                  <Link
                    to="/signup"
                    className={`mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium ${
                      plan.highlight
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>
                <div className="px-6 pt-6 pb-8 border-t border-gray-200 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-900 tracking-wide uppercase">
                    What's included
                  </h4>
                  <ul className="mt-4 space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex">
                        <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="ml-3 text-sm text-gray-700">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {plan.notIncluded.length > 0 && (
                    <>
                      <h4 className="mt-6 text-sm font-semibold text-gray-900 tracking-wide uppercase">
                        Not included
                      </h4>
                      <ul className="mt-4 space-y-3">
                        {plan.notIncluded.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex">
                            <X className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            <span className="ml-3 text-sm text-gray-500">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Calculate Your ROI
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              See how much time and money you can save with TrackerSheets
            </p>
            <button
              onClick={() => setShowCalculator(!showCalculator)}
              className="mt-6 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Calculator className="mr-2 h-5 w-5" />
              {showCalculator ? 'Hide ROI Calculator' : 'Show ROI Calculator'}
            </button>
          </div>

          {showCalculator && (
            <div className="mt-8">
              <ROICalculator />
            </div>
          )}
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-3 bg-blue-50 text-lg font-medium text-gray-900">
              Enterprise Solutions
            </span>
          </div>
        </div>
      </div>

      {/* Enterprise Options */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Enterprise Solutions
            </h2>
            <p className="mt-4 text-xl text-gray-500">
              Scalable solutions for large organizations with custom deployment
              options
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {enterpriseOptions.map((option, index) => (
              <div
                key={index}
                className="relative bg-white rounded-lg shadow-lg overflow-hidden"
              >
                {option.badge && (
                  <div className="absolute top-0 inset-x-0 px-4 py-1 bg-blue-600 text-white text-center text-sm font-semibold">
                    {option.badge}
                  </div>
                )}
                <div className={`px-6 py-8 ${option.badge ? 'pt-12' : ''}`}>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {option.name}
                  </h3>
                  <p className="mt-2 text-gray-500">{option.description}</p>
                  <div className="mt-6">
                    <span className="text-3xl font-extrabold text-gray-900">
                      {option.pricing}
                    </span>
                    {option.subtitle && (
                      <p className="mt-2 text-sm text-gray-500">
                        {option.subtitle}
                      </p>
                    )}
                  </div>
                  <Link
                    to="/contact"
                    className="mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Schedule Consultation
                  </Link>
                </div>
                <div className="px-6 pt-6 pb-8 border-t border-gray-200 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-900 tracking-wide uppercase">
                    Enterprise Features
                  </h4>
                  <ul className="mt-4 space-y-3">
                    {option.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex">
                        <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="ml-3 text-sm text-gray-700">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-blue-900 mb-2">
                Not sure which enterprise option is right for you?
              </h3>
              <p className="text-blue-800 mb-4">
                Our enterprise specialists will help you choose the best
                deployment model and create a custom quote based on your
                specific requirements.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Talk to Enterprise Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Can't find the answer you're looking for? Contact our{' '}
              <Link to="/contact" className="text-blue-600 font-medium">
                sales team
              </Link>
              .
            </p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {[
              {
                question: 'How does the free trial work?',
                answer:
                  'All plans include a 14-day free trial with full access to all features. No credit card required to start. You can upgrade to a paid plan anytime during or after your trial.',
              },
              {
                question: 'Can I change plans later?',
                answer:
                  "Yes, you can upgrade or downgrade your plan at any time. When upgrading, you'll be billed the prorated difference immediately. When downgrading, the change will take effect at the start of your next billing cycle.",
              },
              {
                question: 'Do I need technical skills to use TrackerSheets?',
                answer:
                  'Not at all. If you can use spreadsheets, you can use TrackerSheets. Our platform is designed to be user-friendly and requires no coding knowledge. We also provide detailed guides and support.',
              },
              {
                question: 'What spreadsheet formats do you support?',
                answer:
                  'We support Google Sheets, Microsoft Excel, CSV files, and most popular spreadsheet formats. You can also export your data to any format you need.',
              },
              {
                question: 'Can I use my own domain?',
                answer:
                  'Yes, Business and Enterprise plans support custom domains. This allows you to provide a branded experience to your clients (e.g., status.yourcompany.com).',
              },
              {
                question: 'How secure is my data?',
                answer:
                  "Very secure. We use industry-standard encryption and security practices. Your spreadsheet data is accessed via secure API connections, and we never store your clients' sensitive information.",
              },
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg px-6 py-5">
                <h3 className="text-lg font-medium text-gray-900">
                  {faq.question}
                </h3>
                <p className="mt-2 text-base text-gray-500">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;